import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PanelLeft, Menu, Building2, CalendarDays, ChevronDown, Check, Search, LogOut,
  User, KeyRound, Settings2, LayoutDashboard,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { fetchFinancialYears } from '../../api/resources';
import { initials } from '../../utils/format';
import Logo from '../ui/Logo';
import ThemeToggle from '../ui/ThemeToggle';
import { homeFor, isManager } from '../../utils/roles';

function DropdownShell({ open, onClose, width = 'w-64', children }) {
  const ref = useRef(null);
  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [onClose]);
  if (!open) return null;
  return (
    <div
      ref={ref}
      className={`absolute right-0 top-full z-50 mt-1.5 origin-top-right animate-slide-up overflow-hidden
                  rounded-lg border border-line bg-surface-raised shadow-pop ${width}`}
    >
      {children}
    </div>
  );
}

function MenuItem({ icon: Icon, label, onClick, danger }) {
  return (
    <button
      className={`flex w-full items-center gap-2.5 px-3.5 py-2 text-left text-sm transition-colors hover:bg-surface-hover ${danger ? 'text-danger-600' : 'text-content'}`}
      onClick={onClick}
    >
      <Icon size={16} className={danger ? 'text-danger-500' : 'text-content-subtle'} />
      {label}
    </button>
  );
}

export default function Topbar({ onToggleSidebar, onOpenDrawer }) {
  const { memberships, companyId, fyId, selectCompany, selectFY, user, currentCompany, logout, role } = useAuth();
  const qc = useQueryClient();
  const nav = useNavigate();
  // Worker logins get a trimmed bar: no financial-year switch (its API is not
  // open to them) and no links into admin screens.
  const isWorker = role === 'WORKER';
  // A manager has no dashboard, no profile and no settings — their home is the
  // order list, so the menu offers only what they can actually open.
  const desk = isManager(role);
  const home = homeFor(role);

  const [companyOpen, setCompanyOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [fyOpen, setFyOpen] = useState(false);
  const [companyQuery, setCompanyQuery] = useState('');

  const { data: fyData } = useQuery({
    queryKey: ['fy', companyId],
    queryFn: () => fetchFinancialYears(companyId),
    enabled: !!companyId && !isWorker,
  });
  const years = fyData?.financialYears || [];
  const activeYear = years.find((y) => y.id === Number(fyId));

  const companyFiltered = memberships.filter((m) =>
    companyQuery ? m.company.name.toLowerCase().includes(companyQuery.toLowerCase()) : true,
  );

  const invalidateAll = () => qc.invalidateQueries();

  return (
    <header className="no-print relative z-40 flex h-14 shrink-0 items-center gap-1.5 border-b border-line bg-surface px-2 sm:gap-2 sm:px-3">
      {/* Narrow windows get a drawer, wide ones collapse the rail in place —
          two different jobs, so two buttons rather than one overloaded one. */}
      <button
        onClick={onOpenDrawer}
        className="btn-icon lg:hidden"
        aria-label="Open menu"
        title="Menu"
      >
        <Menu size={20} />
      </button>
      <button
        onClick={onToggleSidebar}
        className="btn-icon hidden lg:inline-flex"
        aria-label="Toggle sidebar"
        title="Toggle sidebar"
      >
        <PanelLeft size={18} />
      </button>

      {/* Brand — single source of truth, lives in the topbar */}
      <Link to={home} className="mr-1 rounded-md px-1 py-1 transition-colors hover:bg-surface-hover">
        <Logo size={30} compact className="hidden sm:flex" />
        <Logo size={30} compact className="sm:hidden [&>span:last-child]:hidden" />
      </Link>

      <div className="ml-auto flex min-w-0 items-center gap-1 sm:gap-1.5">
        {/* Company switcher */}
        <div className="relative">
          <button
            onClick={() => setCompanyOpen((v) => !v)}
            className="flex items-center gap-2 rounded-md border border-line bg-surface px-2 py-1.5 text-sm font-medium text-content shadow-xs transition-colors hover:bg-surface-hover sm:px-2.5"
          >
            <Building2 size={15} className="shrink-0 text-brand-600" />
            <span className="hidden max-w-[150px] truncate sm:block">{currentCompany?.company?.name || 'Select Company'}</span>
            <ChevronDown size={13} className="text-content-subtle" />
          </button>
          <DropdownShell open={companyOpen} onClose={() => setCompanyOpen(false)} width="w-[min(20rem,calc(100vw-1rem))]">
            <div className="flex items-center gap-2 border-b border-line p-2">
              <Search size={15} className="ml-1 text-content-subtle" />
              <input
                className="w-full bg-transparent py-1 text-sm outline-none placeholder:text-content-subtle"
                placeholder="Search companies…"
                value={companyQuery}
                onChange={(e) => setCompanyQuery(e.target.value)}
              />
            </div>
            <ul className="max-h-72 overflow-y-auto py-1">
              {companyFiltered.length === 0 && <p className="px-4 py-3 text-xs text-content-subtle">No matching companies.</p>}
              {companyFiltered.map((m) => (
                <li key={m.company.id}>
                  <button
                    className={`flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-sm transition-colors hover:bg-surface-sunken ${Number(companyId) === m.company.id ? 'bg-brand-50' : ''}`}
                    onClick={() => {
                      if (Number(companyId) !== m.company.id) selectCompany(m.company.id).then(invalidateAll);
                      setCompanyOpen(false);
                    }}
                  >
                    <span className="min-w-0">
                      <span className={`block truncate ${Number(companyId) === m.company.id ? 'font-semibold text-brand-800' : 'text-content'}`}>{m.company.name}</span>
                      <span className="block truncate text-xs text-content-subtle">{m.role} · GST {m.company.gstin || '—'}</span>
                    </span>
                    {Number(companyId) === m.company.id && <Check size={16} className="shrink-0 text-brand-600" />}
                  </button>
                </li>
              ))}
            </ul>
          </DropdownShell>
        </div>

        {/* FY switcher */}
        {!isWorker && (
        <div className="relative">
          <button
            onClick={() => setFyOpen((v) => !v)}
            className="flex items-center gap-2 rounded-md border border-line bg-surface px-2 py-1.5 text-sm font-medium text-content shadow-xs transition-colors hover:bg-surface-hover sm:px-2.5"
          >
            <CalendarDays size={15} className="shrink-0 text-accent-500" />
            <span className="hidden max-w-[140px] truncate md:block">{activeYear?.label || 'Financial Year'}</span>
            <ChevronDown size={13} className="text-content-subtle" />
          </button>
          <DropdownShell open={fyOpen} onClose={() => setFyOpen(false)} width="w-60">
            <p className="border-b border-line px-4 py-2 text-[11px] font-bold uppercase tracking-widest text-content-subtle">Financial Year</p>
            {years.length === 0 && <p className="px-4 py-3 text-xs text-content-subtle">No financial years.</p>}
            {years.map((y) => (
              <button
                key={y.id}
                className={`flex w-full items-center justify-between px-4 py-2.5 text-left text-sm transition-colors hover:bg-surface-sunken ${Number(fyId) === y.id ? 'font-semibold text-brand-800' : 'text-content'}`}
                onClick={() => {
                  if (Number(fyId) !== y.id) selectFY(y.id).then(invalidateAll);
                  setFyOpen(false);
                }}
              >
                {y.label}
                {Number(fyId) === y.id && <Check size={16} className="text-brand-600" />}
              </button>
            ))}
          </DropdownShell>
        </div>
        )}

        {/* Light / dark switch */}
        <ThemeToggle />

        <span className="mx-0.5 hidden h-5 w-px bg-line sm:block" aria-hidden="true" />

        {/* Avatar menu */}
        <div className="relative">
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="flex h-8 w-8 items-center justify-center rounded-full chip-brand text-2xs font-bold transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-1"
            aria-label="Account menu"
          >
            {initials(user?.name)}
          </button>
          <DropdownShell open={menuOpen} onClose={() => setMenuOpen(false)} width="w-[min(15rem,calc(100vw-1rem))]">
            <div className="flex items-center gap-2.5 border-b border-line px-3.5 py-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full chip-brand text-2xs font-bold">
                {initials(user?.name)}
              </span>
              <span className="min-w-0">
                <p className="truncate text-sm font-semibold text-content">{user?.name}</p>
                <p className="truncate text-xs text-content-subtle">
                  {currentCompany?.role || '—'} · {currentCompany?.company?.name || ''}
                </p>
              </span>
            </div>
            <div className="py-1">
              <MenuItem icon={LayoutDashboard} label={isWorker ? 'My Dashboard' : desk ? 'Orders' : 'Dashboard'} onClick={() => { setMenuOpen(false); nav(home); }} />
              {!isWorker && !desk && <MenuItem icon={User} label="Profile" onClick={() => { setMenuOpen(false); nav('/profile'); }} />}
              <MenuItem icon={KeyRound} label="Change Password" onClick={() => { setMenuOpen(false); nav('/change-password'); }} />
              {!isWorker && !desk && <MenuItem icon={Settings2} label="Settings" onClick={() => { setMenuOpen(false); nav('/settings'); }} />}
            </div>
            <div className="border-t border-line py-1">
              <MenuItem icon={LogOut} label="Sign Out" danger onClick={() => { setMenuOpen(false); logout(); nav('/login'); }} />
            </div>
          </DropdownShell>
        </div>
      </div>
    </header>
  );
}
